// Role permission philosophy:
// - workspace_reader: read everywhere, no mutations.
// - workspace_writer: read everywhere plus the data-plane verbs that operate on
//   an already-deployed schema. Writers cannot store/deploy schemas, manage
//   settings, or create/update/delete ledgers and links — those are admin-only.
// - workspace_admin: every permission.
// - platform_admin / platform_access_manager: workspace and member management.
//   There is no dedicated invite resource; workspace_member:invite covers
//   creating, deleting, and updating invites.

import type { RoleKey } from '.';
import type { Permission, Resource } from './permissions';
import { RESOURCE_ACTIONS } from './permissions';

const allPermissions: readonly Permission[] = (
  Object.keys(RESOURCE_ACTIONS) as Resource[]
).flatMap((resource) =>
  RESOURCE_ACTIONS[resource].map(
    (action) => `${resource}:${action}` as Permission
  )
);

const readerPerms = allPermissions.filter((p) => p.endsWith(':read'));

const writerPerms: readonly Permission[] = [
  ...readerPerms,
  'ledger:post_entry',
  'link:sync',
  'custom_currency:create',
];

const platformAdminPerms: readonly Permission[] = [
  'workspace:read',
  'workspace:update',
  'workspace_member:invite',
  'workspace_member:delete',
  'workspace_member:read',
  'user:read',
  'user:invite',
  'user:create',
];
const platformAccessManagerPerms: readonly Permission[] = [
  'workspace_member:invite',
  'workspace_member:delete',
  'workspace_member:read',
  'workspace:update',
  'workspace:read',
];

export const rolePermissions: Readonly<
  Record<RoleKey, ReadonlySet<Permission>>
> = {
  workspace_reader: new Set(readerPerms),
  workspace_writer: new Set(writerPerms),
  workspace_admin: new Set(allPermissions),
  platform_admin: new Set(platformAdminPerms),
  platform_access_manager: new Set(platformAccessManagerPerms),
};
